import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuditLogService } from '../../../../../shared/services/audit-log/audit-log.service';
import { AuditLog } from '../../../../../shared/models/audit-log.model';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { MenuComponentBase } from '../../../../../shared/base-classes/menu-component-base';
import { PopupComponent } from '../../../../../shared/components/popup/popup.component';
import { SvgIconComponent } from '../../../../../shared/components/svg-icon/svg-icon.component';
import {
  ActiveFilter,
  FilterGroupComponent,
} from '../../../../../shared/components/filter-group/filter-group.component';
import {
  TableComponent,
  TableColumn,
} from '../../../../../shared/components/table/table.component';
import { ManageConnectionService } from '../../../../manage-connection-group/services/manage-connection.service';
import { ManageUsersService } from '../../../../manage-users/services/manage-users.service';
import { Connection } from '../../../../manage-connection-group/models/connection';
import { UserDetails } from '../../../../../shared/models/user';
import { Auth0Connection } from '../../../../../shared/models/auth0-connection';
import { PageSizeComponent } from '../../../../../shared/components/page-size/page-size.component';
@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationComponent,
    FilterGroupComponent,
    PopupComponent,
    SvgIconComponent,
    TableComponent,
    PageSizeComponent,
  ],
  providers: [DatePipe],
  templateUrl: './audit-logs.component.html',
  styleUrls: ['./audit-logs.component.scss'],
})
export class AuditLogsComponent extends MenuComponentBase implements OnInit {
  @ViewChild(FilterGroupComponent) filterGroup!: FilterGroupComponent;

  auditLogs: AuditLog[] = [];
  totalCount = 0;
  isLoadingResults = true;
  currentPage = 1;
  pageSize = 200;

  columns: TableColumn[] = [
    { header: 'Date', field: 'timestamp', width: '180px' },
    { header: 'User ', field: 'user' },
    { header: 'Connection', field: 'connection_name' },
    { header: 'Source', field: 'source', width: '100px' },
    { header: 'Feature', field: 'feature_name' },
    { header: 'Action', field: 'action', width: '100px' },
    { header: 'IP Address', field: 'ip_address', width: '130px' },
    { header: 'Description', field: 'description' },
    { header: 'Info', width: '60px', style: { textAlign: 'center' } },
  ];

  // Filter Models
  startDate = '';
  endDate = '';
  userId = '';
  connectionName = '';
  source = '';

  // Applied Filters
  appliedStartDate = '';
  appliedEndDate = '';
  appliedUserId = '';
  appliedConnectionName = '';
  appliedSource = '';

  // Dynamic Filter Data
  connections: Connection[] = [];
  users: UserDetails[] = [];

  // Meta Popup
  showMetaPopup = false;
  selectedMeta: unknown = null;
  copySuccess = false;

  // Validation
  dateValidationError = '';

  // Services
  private auditLogService = inject(AuditLogService);
  private datePipe = inject(DatePipe);
  private manageConnectionService = inject(ManageConnectionService);
  private manageUsersService = inject(ManageUsersService);
  private router = inject(Router);

  override ngOnInit(): void {
    super.ngOnInit();
    this.getConnections();
    this.getUsers();

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.appliedStartDate = params['start_date'] || '';
        this.appliedEndDate = params['end_date'] || '';
        this.appliedUserId = params['user_id'] || '';
        this.appliedConnectionName = params['connection_name'] || '';
        this.appliedSource = params['source'] || '';
        this.currentPage = params['page'] ? +params['page'] : 1;
        this.pageSize = params['page_size'] ? +params['page_size'] : 200;

        // Sync local state with applied filters
        this.startDate = this.appliedStartDate;
        this.endDate = this.appliedEndDate;
        this.userId = this.appliedUserId;
        this.connectionName = this.appliedConnectionName;
        this.source = this.appliedSource;

        this.loadData();
      });
  }

  getConnections(): void {
    this.manageConnectionService
      .getConnectionsFromDB()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: Auth0Connection[]) => {
          this.connections = response.map((c) => ({
            id: c.connection_id,
            name: c.connection_name,
            display_name: c.connection_display_name,
          })) as Connection[];
        },
        error: (err) => {
          console.error('Failed to get connections:', err);
        },
      });
  }

  getUsers(): void {
    const connections = this.connectionName ? [this.connectionName] : [];
    this.manageUsersService
      .getAllUsers(1, 100, connections, '', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.users = response.results;
        },
        error: (err) => {
          console.error('Failed to get users:', err);
        },
      });
  }

  onConnectionFilterChange(): void {
    this.userId = ''; // Reset user selection when connection changes
    this.getUsers();
  }

  loadData() {
    this.isLoadingResults = true;
    const filters: Record<string, string | number> = {};
    if (this.appliedUserId) filters['user_id'] = this.appliedUserId;

    if (this.appliedConnectionName)
      filters['connection_name'] = this.appliedConnectionName;
    if (this.appliedSource) filters['source'] = this.appliedSource;
    if (this.appliedStartDate) {
      filters['start_date'] =
        this.datePipe.transform(this.appliedStartDate, 'yyyy-MM-dd') || '';
    }
    if (this.appliedEndDate) {
      filters['end_date'] =
        this.datePipe.transform(this.appliedEndDate, 'yyyy-MM-dd') || '';
    }

    this.auditLogService
      .getAuditLogs(this.currentPage, this.pageSize, filters)
      .pipe(
        takeUntil(this.destroy$),
        map((data) => {
          if (data === null) return { count: 0, results: [] };
          return data;
        }),
        catchError(() => {
          return of({ count: 0, results: [] });
        }),
      )
      .subscribe((data) => {
        this.isLoadingResults = false;
        this.totalCount = data.count;
        this.auditLogs = data.results;
      });
  }

  onPageChange(page: number) {
    this.updateQueryParams({ page: page });
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.updateQueryParams({ page_size: newPageSize, page: 1 });
    }
  }

  applyFilters(): void {
    // Reset validation error
    this.dateValidationError = '';

    // Validate date range
    if (this.startDate && this.endDate) {
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      if (start > end) {
        this.dateValidationError =
          'Start date must be less than or equal to end date';
        return;
      }
    }

    if (this.filterGroup) {
      this.filterGroup.closeFilter();
    }

    this.updateQueryParams({
      start_date: this.startDate,
      end_date: this.endDate,
      user_id: this.userId,
      connection_name: this.connectionName,
      source: this.source,
      page: 1,
    });
  }

  clearFilters(): void {
    this.startDate = '';
    this.endDate = '';
    this.userId = '';
    this.connectionName = '';
    this.source = '';

    this.appliedStartDate = '';
    this.appliedEndDate = '';
    this.appliedUserId = '';
    this.appliedConnectionName = '';
    this.appliedSource = '';

    this.dateValidationError = '';
    this.getUsers(); // Reset users list to all

    this.updateQueryParams({
      start_date: null,
      end_date: null,
      user_id: null,
      connection_name: null,
      source: null,
      page: 1,
    });
  }

  removeFilter(filter: ActiveFilter): void {
    let paramKey = '';
    switch (filter.type) {
      case 'startDate':
        this.startDate = '';
        paramKey = 'start_date';
        break;
      case 'endDate':
        this.endDate = '';
        paramKey = 'end_date';
        break;
      case 'userId':
        this.userId = '';
        paramKey = 'user_id';
        break;
      case 'connectionName':
        this.connectionName = '';
        paramKey = 'connection_name';
        this.onConnectionFilterChange();
        break;
      case 'source':
        this.source = '';
        paramKey = 'source';
        break;
    }

    const params: Record<string, string | number | null> = {
      page: 1,
    };

    if (filter.type === 'connectionName') {
      params['connection_name'] = null;
      params['user_id'] = null;
    } else {
      params[paramKey] = null;
    }

    this.updateQueryParams(params);
  }

  private updateQueryParams(params: Record<string, string | number | null>) {
    const cleanParams: Record<string, string | number | null> = { ...params };

    // Remove empty values to keep URL clean
    Object.keys(cleanParams).forEach((key) => {
      if (cleanParams[key] === '' || cleanParams[key] === null) {
        cleanParams[key] = null;
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: cleanParams,
      queryParamsHandling: 'merge',
    });
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.appliedStartDate ||
      this.appliedEndDate ||
      this.appliedUserId ||
      this.appliedConnectionName ||
      this.appliedSource
    );
  }

  get activeFilters(): ActiveFilter[] {
    const filters: ActiveFilter[] = [];
    if (this.appliedStartDate) {
      filters.push({
        type: 'startDate',
        label: 'Start Date',
        value: this.appliedStartDate,
        displayValue:
          this.datePipe.transform(this.appliedStartDate, 'dd-MM-yyyy') ||
          this.appliedStartDate,
      });
    }
    if (this.appliedEndDate) {
      filters.push({
        type: 'endDate',
        label: 'End Date',
        value: this.appliedEndDate,
        displayValue:
          this.datePipe.transform(this.appliedEndDate, 'dd-MM-yyyy') ||
          this.appliedEndDate,
      });
    }
    if (this.appliedConnectionName) {
      const conn = this.connections.find(
        (c) => c.name === this.appliedConnectionName,
      );
      filters.push({
        type: 'connectionName',
        label: 'Connection',
        value: this.appliedConnectionName,
        displayValue: conn
          ? conn.display_name || conn.name
          : this.appliedConnectionName,
      });
    }
    if (this.appliedSource) {
      filters.push({
        type: 'source',
        label: 'Source',
        value: this.appliedSource,
        displayValue: this.appliedSource,
      });
    }
    if (this.appliedUserId) {
      const user = this.users.find(
        (u) => u.id.toString() === this.appliedUserId,
      );
      filters.push({
        type: 'userId',
        label: 'User',
        value: this.appliedUserId,
        displayValue: user
          ? `${user.first_name} ${user.last_name}`
          : this.appliedUserId,
      });
    }
    return filters;
  }

  openMetaPopup(logId: number): void {
    this.auditLogService
      .getMetaDetails(logId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res !== null) {
          this.selectedMeta = res;
        } else {
          this.selectedMeta = null;
        }
        this.showMetaPopup = true;
      });
  }

  closeMetaPopup(): void {
    this.showMetaPopup = false;
    this.selectedMeta = null;
    this.copySuccess = false;
  }

  copyToClipboard(): void {
    if (this.selectedMeta) {
      const jsonStr = JSON.stringify(this.selectedMeta, null, 2);
      navigator.clipboard.writeText(jsonStr).then(() => {
        this.copySuccess = true;
        setTimeout(() => (this.copySuccess = false), 2000);
      });
    }
  }
}
